/**
 * HOW BIG THE PHONE IS DRAWN ON A DESKTOP, from the window alone (owner feedback: "on browser it's very
 * tiny, all of it").
 *
 * The phone is designed at one size — 412 CSS px wide, the item's casing round a phone-shaped screen —
 * and a desktop gets that design ZOOMED, never re-laid-out: `zoom` scales the casing, the type, the
 * sprites and every app inside together, and the screen keeps its 412 px of layout, so the apps (and
 * their container queries) see the same phone on a laptop as on a 4K monitor. The zoom follows the
 * window's height from the size it was drawn at (900 px tall: zoom 1) up, so a 1440p or 4K screen sees a
 * phone filling most of its height rather than a stamp in the middle of it. It never shrinks the type
 * below the drawn size: a short window (a 1366×768 laptop) drops the antenna and the stub first, and
 * the body takes the room they had.
 *
 * Phones, and windows too small for any casing, get the screen edge to edge instead (`full`), which
 * phone.css draws with media queries of the same thresholds.
 *
 * Pure, so the numbers are unit-tested rather than eyeballed; `PhoneShell` feeds it `innerWidth` and
 * `innerHeight` on every resize.
 */

/** The casing's width, in the design's own CSS px. */
export const PHONE_WIDTH = 412;
/** The window height the phone was drawn for, where the zoom is exactly 1. */
export const DESIGN_WINDOW_HEIGHT = 900;
/** How far the antenna (above) and the stub (below) show beyond the body, in design px. */
export const ANTENNA_ROOM = 114;
export const STUB_ROOM = 72;
/** Breathing room above and below the phone, in design px. */
export const FIT_MARGIN = 20;
/** The screen is never shorter than this; a window that cannot give it scrolls rather than shrinking type. */
export const BODY_MIN = 520;
/** ...nor taller than this: past it a phone stops looking like one. */
export const BODY_MAX = 860;
/** The antenna and the stub are drawn only when the body still gets this much beside them. */
export const BODY_MIN_WITH_DECOR = 600;
/** A 4K screen at 100 % is 2160 px tall; past this the phone would be a poster. */
export const MAX_ZOOM = 2.6;
/** At or below either, the phone IS the screen (phone.css's media queries use the same two). */
export const FULL_SCREEN_MAX_WIDTH = 560;
export const FULL_SCREEN_MAX_HEIGHT = 520;

export interface PhoneFit {
  /** `device`: the casing on the hub backdrop; `full`: the screen edge to edge, as on a phone. */
  readonly mode: 'device' | 'full';
  /** CSS `zoom` for the device and its caption; 1 in `full`. */
  readonly zoom: number;
  /** The body's height in design px (before the zoom), or 0 in `full`, where CSS sizes it. */
  readonly bodyHeight: number;
  /** Whether the antenna and the stub are drawn. */
  readonly decor: boolean;
}

export function phoneFit(width: number, height: number): PhoneFit {
  if (!(width > FULL_SCREEN_MAX_WIDTH && height > FULL_SCREEN_MAX_HEIGHT)) {
    return { mode: 'full', zoom: 1, bodyHeight: 0, decor: false };
  }
  // Grow with the window's height, never below the drawn size, never wider than the window allows.
  const byHeight = Math.min(MAX_ZOOM, Math.max(1, height / DESIGN_WINDOW_HEIGHT));
  const byWidth = (width - 48) / PHONE_WIDTH;
  const zoom = Math.max(1, Math.min(byHeight, byWidth));
  // What the window gives the phone, in design px, once the margins are taken.
  const room = height / zoom - 2 * FIT_MARGIN;
  const decor = room - ANTENNA_ROOM - STUB_ROOM >= BODY_MIN_WITH_DECOR;
  const body = decor ? room - ANTENNA_ROOM - STUB_ROOM : room;
  return {
    mode: 'device',
    zoom: Math.round(zoom * 1000) / 1000,
    bodyHeight: Math.floor(Math.min(BODY_MAX, Math.max(BODY_MIN, body))),
    decor,
  };
}
